import * as React from "react"

import type { VariantProps } from "class-variance-authority"

import { Button, buttonVariants } from "./Button"
import {
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  DialogBodyV2,
} from "./Dialog"
import type { PermissionGateProps } from "./PermissionTooltip"
import { Tooltip } from "./Tooltip"

type ConfirmationDialogProps = PermissionGateProps & {
  title: React.ReactNode
  description?: React.ReactNode
  onOpenAutoFocus?: (event: Event) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onConfirm: () => void
  onCancel?: () => void
  trigger?: React.ReactNode
  disabled?: boolean
  disabledTooltip?: string
  variant?: VariantProps<typeof buttonVariants>["variant"]
  confirmText?: string
  loading?: boolean
  children?: React.ReactNode
}

// TODO: Consider making a deletion variant of this dialog
export const ConfirmationDialog = ({
  title,
  description,
  onOpenAutoFocus,
  confirmText = "Confirm",
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  trigger,
  disabled,
  disabledTooltip,
  variant,
  loading,
  requires,
  permissionReason,
  allowedTitle,
  tooltipSide,
  wrapperClassName,
  children,
}: ConfirmationDialogProps) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange?.(nextOpen)
    setIsOpen(nextOpen)
  }

  const handleConfirm = () => {
    onConfirm()
    // When controlled (open prop), let the parent close via onOpenChange after async work
    if (open === undefined) {
      handleOpenChange(false)
    }
  }

  const handleCancel = () => {
    onCancel?.()
    handleOpenChange(false)
  }

  // With `requires` the gated trigger owns the single tooltip, so `disabledTooltip` becomes its
  // `allowedTitle` — but only while `disabled`, since that prop explains a disabled state rather
  // than annotating an enabled one. The permission reason wins while blocked. Without `requires`
  // a `Tooltip` still has to sit outside the trigger to open at all: it fires no pointer events
  // once disabled.
  const gatedTrigger = trigger && (
    <DialogTrigger
      asChild
      disabled={disabled}
      requires={requires}
      permissionReason={permissionReason}
      allowedTitle={allowedTitle ?? (disabled ? disabledTooltip : undefined)}
      tooltipSide={tooltipSide}
      wrapperClassName={wrapperClassName}
    >
      {trigger}
    </DialogTrigger>
  )

  return (
    <DialogRoot open={open ?? isOpen} onOpenChange={handleOpenChange}>
      {requires === undefined && disabled && disabledTooltip ? (
        <Tooltip title={disabledTooltip}>{gatedTrigger}</Tooltip>
      ) : (
        gatedTrigger
      )}
      <DialogContentV2
        closeButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onOpenAutoFocus={onOpenAutoFocus}
      >
        <DialogHeaderV2>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2>
          {description && <div className="text-fg-subtle paragraph-base">{description}</div>}
          {children}
        </DialogBodyV2>
        <DialogFooterV2 className="gap-3">
          <DialogClose asChild>
            <Button variant="transparent" onClick={handleCancel}>
              Cancel
            </Button>
          </DialogClose>
          <div className="flex-1" />
          <Button
            variant={variant ?? undefined}
            onClick={handleConfirm}
            loading={loading}
            requires={requires}
            permissionReason={permissionReason}
          >
            {confirmText}
          </Button>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
